import { useContext, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useRouteMatch } from '@fs/zion-router'
import { getQueueFamily } from '../../../../services/QueuesService'
import { useQueryParams } from '../../../../util/UrlUtil'
import { queueNames, navigationQueryParams } from '../../messagesList/useMessagesListController'
import { QueuesContext } from '../../QueuesContext'
import { useQueueButtons } from '../../messagesList/hooks/useQueueButtons'
import { getMessageById } from '../../../../services/dmqueuesService'
import useNotifications from '../../../../hooks/useNotifications'

export function useFamilyDetailsState(
  backToMessagesList,
  showViewButton,
  showButtonsJustPrimary,
  personLinkInNewTab,
  goBackAfterButtonCallback,
  showDeleteMessageButton,
  showAncestry
) {
  const pathParams = useParams()
  const queryParams = useQueryParams()
  const match = useRouteMatch()
  const { goToUrlAndAppendYadVashemTab } = useContext(QueuesContext)
  const { queueButtons } = useQueueButtons()
  const { error } = useNotifications()
  const { t } = useTranslation()

  const initialState = {
    principalPersonId: null,
    message: null,
    queueName: pathParams.queueName,
    queueFamily: null,
    showViewButton: showViewButton == null ? true : showViewButton,
    showButtonsJustPrimary: showButtonsJustPrimary == null ? false : showButtonsJustPrimary,
    showDeleteMessageButton: showDeleteMessageButton == null ? false : showDeleteMessageButton,
    showAncestry: showAncestry == null ? false : showAncestry,
    personLinkInNewTab,
    goBackAfterButtonCallback,
    viewButtonsVisited: [],
  }

  const reducer = (_, currentState) => {
    return { ...currentState }
  }

  const [familyDetailsState, setFamilyDetailsState] = useReducer(reducer, initialState)

  const familyDetailsActions = {
    load: async () => {
      return getMessageById(pathParams.messageId)
        .then((message) => {
          familyDetailsActions.setMessage(message.data)
          // If personId comes from the queryParams, then it is a new tab; otherwise, we are coming from the message
          familyDetailsActions.lookForFamily(queryParams?.get('personId') || message.data.details.personId)
          return Promise.resolve()
        })
        .catch((err) => {
          error(
            t('queues.errors.loadingMessage', {
              defaultValue: 'Error loading message {{messageId}}',
              messageId: pathParams.messageId,
            }),
            err
          )
        })
    },
    setMessage: (message) => {
      familyDetailsState.message = message
      familyDetailsActions.updateFamilyDetailsState()
    },
    deleteMessage: async () => {
      return queueButtons.deleteMessageById({ message: familyDetailsState.message }).then(() => {
        backToMessagesList()
        return Promise.resolve()
      })
    },
    setPrincipalPersonId: (personId) => {
      familyDetailsState.principalPersonId = personId.toUpperCase()
      familyDetailsActions.updateFamilyDetailsState()
    },
    lookForFamilyInNewTab: (personId) => {
      const url = `${match.url}?${navigationQueryParams({
        queuePageIndex: queryParams.get('queuePageIndex'),
        personId,
        treeCategories: queryParams.get('treeCategories'),
      })}`
      if (pathParams.queueName.includes(queueNames.HOLOCAUST_QUEUES)) {
        goToUrlAndAppendYadVashemTab(window, url, 'thisIsTheYadVashemTab')
      } else {
        window.open(url, '_blank')
      }
      return Promise.resolve(url)
    },
    lookForFamily: async (personId) => {
      return getQueueFamily(personId)
        .then((data) => {
          data.principal.isPrincipal = true
          familyDetailsActions.setPrincipalPersonId(data.principal.personId)
          familyDetailsActions.setQueueFamily(data)
          return Promise.resolve()
        })
        .catch((err) => {
          error(
            t('queues.errors.loadingFamily', {
              defaultValue: 'Error finding person {{personId}}',
              personId,
            }),
            err
          )
        })
    },
    renderModalDialog: (person, dialog) => {
      return dialog(
        familyDetailsState.message,
        person.personId,
        person.name,
        person.isPrincipal && familyDetailsState.goBackAfterButtonCallback
          ? backToMessagesList
          : () => {
              familyDetailsActions.lookForFamily(familyDetailsState.principalPersonId)
            },
        null
      )
    },
    isPrincipalPage: () => {
      return familyDetailsState.message.details.personId === familyDetailsState.principalPersonId
    },
    callButtonCallback: async (person, buttonCallback) => {
      const isPromise = buttonCallback({ message: familyDetailsState.message, person })
      // if it is not a promise, then it is a button like yad vashem
      if (isPromise.then) {
        return isPromise.then(() => {
          // reload to see the labels
          familyDetailsActions.lookForFamily(familyDetailsState.principalPersonId)
          // Only go back if person is principal, and this person is in the principal position in the page
          if (
            person.isPrincipal &&
            familyDetailsActions.isPrincipalPage() &&
            familyDetailsState.goBackAfterButtonCallback
          ) {
            backToMessagesList()
          }
        })
      }
      return Promise.resolve()
    },
    setQueueFamily: (queueFamily) => {
      familyDetailsState.queueFamily = queueFamily
      familyDetailsActions.updateFamilyDetailsState()
    },
    updateFamilyDetailsState: () => {
      setFamilyDetailsState(familyDetailsState)
    },
    updateViewButtonsVisited: (personId) => {
      familyDetailsState.viewButtonsVisited = [...familyDetailsState.viewButtonsVisited, personId]
      familyDetailsActions.updateFamilyDetailsState()
    },
  }

  return { familyDetailsState, familyDetailsActions }
}
